/**
 * HBCU Media — discover and stream HBCU radio stations and podcasts
 * in-app.  Audio keeps playing as users navigate elsewhere.
 *
 * Radio:   Radio Browser API (free, no key, CORS-safe)
 * Podcasts: iTunes Search API + iTunes Lookup for episodes (free, no key)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAudio } from '../contexts/AudioContext';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Search, Play, Pause, Radio, Mic2, ChevronLeft,
  Clock, Calendar, ExternalLink, Loader2,
  Music, RefreshCw, AlertCircle, Users,
  ChevronDown, ChevronUp, X, Plus, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';
import { radioAPI, creatorsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// ── HBCU school list for submission dropdowns ─────────────────────────────────
const HBCU_SCHOOLS = [
  'Alabama A&M University',
  'Alabama State University',
  'Alcorn State University',
  'Bethune-Cookman University',
  'Bowie State University',
  'Central State University',
  'Cheyney University',
  'Clark Atlanta University',
  'Coppin State University',
  'Delaware State University',
  'Dillard University',
  'Elizabeth City State University',
  'Fayetteville State University',
  'Florida A&M University',
  'Fort Valley State University',
  'Grambling State University',
  'Hampton University',
  'Howard University',
  'Huston-Tillotson University',
  'Jackson State University',
  'Johnson C. Smith University',
  'Kentucky State University',
  'Langston University',
  'Lincoln University (Missouri)',
  'Lincoln University (Pennsylvania)',
  'Livingstone College',
  'Meharry Medical College',
  'Miles College',
  'Mississippi Valley State University',
  'Morehouse College',
  'Morgan State University',
  'NC A&T State University',
  'NC Central University',
  'Norfolk State University',
  'Oakwood University',
  'Prairie View A&M University',
  'Shaw University',
  'Rust College',
  'Savannah State University',
  'Spelman College',
  'Southern University',
  'Tennessee State University',
  'Texas Southern University',
  'Tuskegee University',
  'University of Arkansas Pine Bluff',
  'University of the District of Columbia',
  'Virginia State University',
  'Virginia Union University',
  'Winston-Salem State University',
  'Xavier University of Louisiana',
  'Other HBCU',
  'Not affiliated with a specific HBCU',
];

// ── Radio Browser API ────────────────────────────────────────────────────────
const RB_BASE = 'https://de1.api.radio-browser.info/json';

async function fetchRadioStations(params = {}) {
  const q = new URLSearchParams({
    limit: '30',
    hidebroken: 'true',
    order: 'clickcount',
    reverse: 'true',
    countrycode: 'US',
    // CRITICAL: GradLink is HTTPS — browsers block HTTP audio streams as
    // mixed content. httpsonly filters to stations with https:// stream URLs.
    httpsonly: 'true',
    ...params,
  });
  const r = await fetch(`${RB_BASE}/stations/search?${q}`);
  if (!r.ok) throw new Error('Radio API error');
  return r.json();
}

// ── iTunes Search API ────────────────────────────────────────────────────────
async function searchPodcasts(term) {
  const q = new URLSearchParams({ term, media: 'podcast', limit: '24', country: 'US' });
  const r = await fetch(`https://itunes.apple.com/search?${q}`);
  if (!r.ok) throw new Error('Podcast search error');
  const data = await r.json();
  return (data.results || []).filter(p => p.collectionId);
}

async function fetchEpisodes(collectionId) {
  const q = new URLSearchParams({
    id: collectionId,
    entity: 'podcastEpisode',
    limit: '20',
    sort: 'recent',
  });
  const r = await fetch(`https://itunes.apple.com/lookup?${q}`);
  if (!r.ok) throw new Error('Episode fetch error');
  const data = await r.json();
  // First result is the podcast itself; rest are episodes
  return (data.results || []).filter(e => e.wrapperType === 'podcastEpisode');
}

// ── Curated HBCU station callsigns ───────────────────────────────────────────
// Searched by name in Radio Browser so we always get the current stream URL.
// Add more callsigns here as new stations are discovered.
const HBCU_STATION_NAMES = [
  // Confirmed HBCU-licensed stations
  'WHUR',  // Howard University, Washington DC
  'WCLK',  // Clark Atlanta University, Atlanta GA
  'WEAA',  // Morgan State University, Baltimore MD
  'WNAA',  // NC A&T State University, Greensboro NC
  'WSHA',  // Shaw University, Raleigh NC
  'WRVS',  // Elizabeth City State University, NC
  'WAMF',  // Xavier University of Louisiana, New Orleans
  'WBEJ',  // Bethune-Cookman University, Daytona Beach FL
  'WDCU',  // University of the District of Columbia
  'WSSL',  // Livingstone College, Salisbury NC
  'WSSU',  // Winston-Salem State University, NC
  'WFSS',  // Fayetteville State University, NC
  'WZZA',  // Tuskegee-area HBCU, Alabama
  'WVSU',  // Virginia State University, Petersburg VA
  'WJZD',  // Mississippi Gulf Coast HBCU-affiliated
  'WGOK',  // Mobile AL HBCU community
  'WZMG',  // Alabama HBCU community
  'KUCA',  // University of Central Arkansas HBCU programming
  // Added from community roll call
  'KPVU',  // Prairie View A&M University, TX
  'WHOV',  // Hampton University, VA
  'KALU',  // Langston University, OK
  'WPRL',  // Alcorn State University, MS
  'WVSD',  // Mississippi Valley State University, MS
  'WJAB',  // Alabama A&M University, AL
  'WOCG',  // Oakwood University, AL (now WJOU)
  'WJOU',  // Oakwood University, AL
  'WVAS',  // Alabama State University, AL
  'WNSB',  // Norfolk State University, VA
  'WDUB',  // Dillard University, LA
  'KJLU',  // Lincoln University Missouri
  'KUAP',  // University of Arkansas at Pine Bluff
  'WWLU',  // Lincoln University PA (was WLIU)
];

// ── College affiliation map ───────────────────────────────────────────────────
// Maps callsign prefix → college metadata shown on the station card.
// Only include callsigns we're confident about; others show no badge.
const HBCU_AFFILIATIONS = {
  WHUR:  { college: 'Howard University',               city: 'Washington, DC',     short: 'Howard' },
  WCLK:  { college: 'Clark Atlanta University',        city: 'Atlanta, GA',        short: 'Clark Atlanta' },
  WEAA:  { college: 'Morgan State University',         city: 'Baltimore, MD',      short: 'Morgan State' },
  WNAA:  { college: 'NC A&T State University',         city: 'Greensboro, NC',     short: 'NC A&T' },
  WSHA:  { college: 'Shaw University',                 city: 'Raleigh, NC',        short: 'Shaw' },
  WRVS:  { college: 'Elizabeth City State University', city: 'Elizabeth City, NC', short: 'ECSU' },
  WAMF:  { college: 'Xavier University of Louisiana',  city: 'New Orleans, LA',    short: 'Xavier (LA)' },
  WBEJ:  { college: 'Bethune-Cookman University',      city: 'Daytona Beach, FL',  short: 'Bethune-Cookman' },
  WDCU:  { college: 'Univ. of District of Columbia',   city: 'Washington, DC',     short: 'UDC' },
  WSSL:  { college: 'Livingstone College',             city: 'Salisbury, NC',      short: 'Livingstone' },
  WSSU:  { college: 'Winston-Salem State University',  city: 'Winston-Salem, NC',  short: 'WSSU' },
  WFSS:  { college: 'Fayetteville State University',   city: 'Fayetteville, NC',   short: 'Fayetteville State' },
  WVSU:  { college: 'Virginia State University',       city: 'Petersburg, VA',     short: 'Virginia State' },
  // Added from community roll call
  KPVU:  { college: 'Prairie View A&M University',     city: 'Prairie View, TX',   short: 'Prairie View A&M' },
  WHOV:  { college: 'Hampton University',              city: 'Hampton, VA',        short: 'Hampton' },
  KALU:  { college: 'Langston University',             city: 'Langston, OK',       short: 'Langston' },
  WPRL:  { college: 'Alcorn State University',         city: 'Lorman, MS',         short: 'Alcorn State' },
  WVSD:  { college: 'Mississippi Valley State Univ.', city: 'Itta Bena, MS',      short: 'Mississippi Valley' },
  WJAB:  { college: 'Alabama A&M University',          city: 'Huntsville, AL',     short: 'Alabama A&M' },
  WJOU:  { college: 'Oakwood University',              city: 'Huntsville, AL',     short: 'Oakwood' },
  WOCG:  { college: 'Oakwood University',              city: 'Huntsville, AL',     short: 'Oakwood' },
  WVAS:  { college: 'Alabama State University',        city: 'Montgomery, AL',     short: 'Alabama State' },
  WNSB:  { college: 'Norfolk State University',        city: 'Norfolk, VA',        short: 'Norfolk State' },
  WDUB:  { college: 'Dillard University',              city: 'New Orleans, LA',    short: 'Dillard' },
  KJLU:  { college: 'Lincoln University (Missouri)',   city: 'Jefferson City, MO', short: 'Lincoln U (MO)' },
  KUAP:  { college: 'Univ. of Arkansas Pine Bluff',   city: 'Pine Bluff, AR',     short: 'UAPB' },
  WWLU:  { college: 'Lincoln University (Pennsylvania)', city: 'Lincoln Univ, PA', short: 'Lincoln U (PA)' },
};

/** Extract call letters from a station name (e.g. "WHUR 96.3 FM Jazz..." → "WHUR") */
function getCallLetters(name) {
  if (!name) return '???';
  const m = name.match(/^([WK][A-Z]{2,3})\b/);
  if (m) return m[1];
  // Fallback: first word, up to 5 chars
  return name.split(/[\s\-–]/)[0].substring(0, 5).toUpperCase();
}

/**
 * Deterministic gradient per callsign so every blank card has its own look.
 * Returns a Tailwind-compatible inline style string.
 */
const FALLBACK_GRADIENTS = [
  'linear-gradient(135deg,#0F3D2B,#1A5C3F)',
  'linear-gradient(135deg,#1A2A4A,#0D3060)',
  'linear-gradient(135deg,#3D1A0F,#5C2A0D)',
  'linear-gradient(135deg,#2A1A4A,#3D0D60)',
  'linear-gradient(135deg,#1A3D3D,#0D5C5C)',
  'linear-gradient(135deg,#3D2A0F,#603D0D)',
  'linear-gradient(135deg,#1A0F3D,#2A0D60)',
  'linear-gradient(135deg,#3D3D0F,#5C5C0D)',
];
function fallbackGradient(callLetters) {
  let hash = 0;
  for (const c of callLetters) hash = (hash * 31 + c.charCodeAt(0)) & 0xff;
  return FALLBACK_GRADIENTS[hash % FALLBACK_GRADIENTS.length];
}

/** Returns the college affiliation for a station name, or null. */
function getAffiliation(stationName) {
  const upper = (stationName || '').toUpperCase();
  for (const [callsign, info] of Object.entries(HBCU_AFFILIATIONS)) {
    if (upper.startsWith(callsign)) return info;
  }
  return null;
}

const HBCU_PODCAST_TERMS = [
  'HBCU', 'Howard University', 'Spelman', 'Morehouse', 'Black college',
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDuration(ms) {
  if (!ms) return '';
  const s  = Math.round(ms / 1000);
  const m  = Math.floor(s / 60);
  const h  = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function relDate(iso) {
  if (!iso) return '';
  try {
    const d   = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7)  return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StationCard({ station, isActive, isPlaying, hasError, affiliation, onPlay }) {
  const tags    = (station.tags || '').split(',').filter(Boolean).slice(0, 3);
  const bitrate = station.bitrate ? `${station.bitrate}kbps` : null;
  const callLetters = getCallLetters(station.name);
  const collegeName = affiliation?.short || '';

  return (
    <div
      className={`group relative flex flex-col rounded-2xl overflow-hidden transition-all border
        ${hasError
          ? 'border-red-300 shadow-md ring-2 ring-red-200'
          : isActive
            ? 'border-mint-fresh shadow-md ring-2 ring-mint-fresh/30'
            : 'border-transparent hover:border-slate-200 hover:shadow-md'}`}
      style={{ backgroundColor: 'hsl(var(--card))' }}
    >
      {/* Artwork */}
      <div className="relative aspect-square overflow-hidden">
        {station.favicon
          ? <>
              {/* Gradient fallback behind the image in case it fails */}
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2"
                style={{ background: fallbackGradient(callLetters) }}
              >
                <span className="text-mint-fresh font-black tracking-tight leading-none"
                  style={{ fontSize: 'clamp(1.4rem, 5cqw, 2rem)' }}>
                  {callLetters}
                </span>
                {collegeName && (
                  <span className="text-white/60 font-medium text-center leading-tight"
                    style={{ fontSize: 'clamp(0.55rem, 2cqw, 0.7rem)' }}>
                    {collegeName}
                  </span>
                )}
              </div>
              <img
                src={station.favicon}
                alt={station.name}
                className="absolute inset-0 w-full h-full object-cover"
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
            </>
          : (
            <div
              className="w-full h-full flex flex-col items-center justify-center gap-1.5 px-2"
              style={{ background: fallbackGradient(callLetters) }}
            >
              <span className="text-mint-fresh font-black tracking-tight leading-none text-center"
                style={{ fontSize: 'clamp(1.6rem, 6cqw, 2.25rem)' }}>
                {callLetters}
              </span>
              {collegeName && (
                <span className="text-white/60 font-medium text-center leading-tight"
                  style={{ fontSize: 'clamp(0.6rem, 2.2cqw, 0.75rem)' }}>
                  {collegeName}
                </span>
              )}
            </div>
          )}

        {/* LIVE badge */}
        {!hasError && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/90 text-white uppercase tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            Live
          </span>
        )}
        {/* Stream error overlay */}
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-dark-slate/70 gap-1 p-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-[10px] text-white text-center leading-tight">Stream unavailable</p>
          </div>
        )}

        {/* Play overlay */}
        <button
          onClick={onPlay}
          className="absolute inset-0 flex items-center justify-center
            bg-dark-slate/0 group-hover:bg-dark-slate/40 transition-colors"
          aria-label={`Play ${station.name}`}
        >
          <div className={`w-12 h-12 rounded-full bg-dark-slate flex items-center justify-center shadow-lg
            transition-all ${isActive ? 'opacity-100 scale-100' : 'opacity-0 group-hover:opacity-100 group-hover:scale-100'}`}>
            {isActive && isPlaying
              ? <Pause  className="w-5 h-5 text-mint-fresh" />
              : <Play   className="w-5 h-5 text-mint-fresh ml-0.5" />}
          </div>
        </button>
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col gap-1">
        <p className="font-semibold text-dark-slate text-sm line-clamp-1">{station.name}</p>

        {/* College affiliation badge */}
        {affiliation && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold
            bg-mint-fresh/20 text-dark-slate w-fit max-w-full">
            🎓 <span className="truncate">{affiliation.short}</span>
          </span>
        )}

        {!affiliation && station.state && (
          <p className="text-xs text-slate-gray truncate">{station.state}</p>
        )}

        <div className="flex flex-wrap gap-1 mt-1">
          {tags.map(tag => (
            <span key={tag} className="px-1.5 py-0.5 rounded-full text-[10px] bg-slate-100 text-slate-500 capitalize">
              {tag.trim()}
            </span>
          ))}
          {bitrate && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700">
              {bitrate}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function PodcastCard({ podcast, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`group text-left flex flex-col rounded-2xl overflow-hidden transition-all border
        ${isSelected
          ? 'border-mint-fresh shadow-md ring-2 ring-mint-fresh/30'
          : 'border-transparent hover:border-slate-200 hover:shadow-md'}`}
      style={{ backgroundColor: 'hsl(var(--card))' }}
    >
      <div className="relative aspect-square overflow-hidden bg-slate-100">
        {podcast.artworkUrl600
          ? <img src={podcast.artworkUrl600} alt={podcast.collectionName} className="w-full h-full object-cover" />
          : (
            <div className="w-full h-full flex items-center justify-center bg-dark-slate">
              <Mic2 className="w-10 h-10 text-mint-fresh/60" />
            </div>
          )}
        <div className="absolute inset-0 bg-dark-slate/0 group-hover:bg-dark-slate/20 transition-colors flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-dark-slate/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Play className="w-4 h-4 text-white ml-0.5" />
          </div>
        </div>
      </div>
      <div className="p-3">
        <p className="font-semibold text-dark-slate text-sm line-clamp-2 leading-tight">{podcast.collectionName}</p>
        <p className="text-xs text-slate-gray mt-0.5 truncate">{podcast.artistName}</p>
        {podcast.trackCount > 0 && (
          <p className="text-xs text-slate-gray mt-1">{podcast.trackCount} episodes</p>
        )}
      </div>
    </button>
  );
}

function EpisodeRow({ episode, isActive, isPlaying, onPlay }) {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-xl transition-colors cursor-pointer group
        ${isActive
          ? 'bg-mint-fresh/10 border border-mint-fresh/30'
          : 'hover:bg-slate-50 border border-transparent'}`}
      onClick={onPlay}
    >
      {/* Play button */}
      <button
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all
          ${isActive
            ? 'bg-dark-slate'
            : 'bg-slate-100 group-hover:bg-dark-slate'}`}
        aria-label={isActive && isPlaying ? 'Pause' : 'Play episode'}
      >
        {isActive && isPlaying
          ? <Pause className="w-3.5 h-3.5 text-mint-fresh" />
          : <Play  className={`w-3.5 h-3.5 ml-0.5 ${isActive ? 'text-mint-fresh' : 'text-slate-500 group-hover:text-mint-fresh'}`} />}
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium line-clamp-2 leading-tight ${isActive ? 'text-dark-slate' : 'text-dark-slate'}`}>
          {episode.trackName}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {episode.releaseDate && (
            <span className="flex items-center gap-1 text-xs text-slate-gray">
              <Calendar className="w-3 h-3" />
              {relDate(episode.releaseDate)}
            </span>
          )}
          {episode.trackTimeMillis && (
            <span className="flex items-center gap-1 text-xs text-slate-gray">
              <Clock className="w-3 h-3" />
              {fmtDuration(episode.trackTimeMillis)}
            </span>
          )}
        </div>
        {episode.description && (
          <p className="text-xs text-slate-gray mt-1 line-clamp-2">{episode.description}</p>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function HBCUMedia() {
  usePageTitle('HBCU Media');

  const { currentTrack, isPlaying, isLoading, error: audioError, playTrack, togglePlay } = useAudio();
  const { user } = useAuth();

  const [activeTab, setActiveTab]         = useState('creators');

  // ── Creators state ───────────────────────────────────────────────────────
  const [creators,          setCreators]          = useState([]);
  const [creatorsLoading,   setCreatorsLoading]   = useState(true);
  const [expandedCreator,   setExpandedCreator]   = useState(null);
  const [creatorVideos,     setCreatorVideos]      = useState({});   // { channelId: [...] }
  const [videosLoading,     setVideosLoading]      = useState({});   // { channelId: bool }
  const [creatorSchool,     setCreatorSchool]      = useState('all');
  const [creatorPlatform,   setCreatorPlatform]   = useState('all');
  const [videoModal,        setVideoModal]         = useState(null); // { embedUrl, title }

  // ── Submission modals ────────────────────────────────────────────────────
  const [showCreatorModal,  setShowCreatorModal]  = useState(false);
  const [showPodcastModal,  setShowPodcastModal]  = useState(false);
  const [submitting,        setSubmitting]         = useState(false);

  const BLANK_CREATOR = { name: '', platform: 'youtube', channel_url: '', handle: '', school: '', description: '', category: '', submitter_name: '', submitter_email: '' };
  const BLANK_PODCAST = { name: '', url: '', platform: 'apple', school: '', description: '', host: '', submitter_name: '', submitter_email: '' };

  const [creatorForm,  setCreatorForm]  = useState(BLANK_CREATOR);
  const [podcastForm,  setPodcastForm]  = useState(BLANK_PODCAST);

  // Curated podcasts (community-approved)
  const [curatedPodcasts, setCuratedPodcasts] = useState([]);

  // Curated HBCU stations (from backend)
  const [curatedStations, setCuratedStations] = useState([]);

  // Radio state
  const [radioQuery,    setRadioQuery]    = useState('');
  const [radioStations, setRadioStations] = useState([]);
  const [radioLoading,  setRadioLoading]  = useState(true);
  const [radioError,    setRadioError]    = useState(null);
  const [radioFilter,   setRadioFilter]   = useState('all'); // 'all'|'jazz'|'gospel'|'r&b'|'hip-hop'
  const [collegeFilter, setCollegeFilter] = useState('all'); // 'all' | college short name

  // Podcast state
  const [podQuery,      setPodQuery]      = useState('');
  const [podcasts,      setPodcasts]      = useState([]);
  const [podLoading,    setPodLoading]    = useState(true);
  const [podError,      setPodError]      = useState(null);
  const [selectedPod,   setSelectedPod]   = useState(null);
  const [episodes,      setEpisodes]      = useState([]);
  const [epLoading,     setEpLoading]     = useState(false);

  const radioInputRef = useRef(null);
  const podInputRef   = useRef(null);

  // ── Load initial radio stations ──────────────────────────────────────────
  const loadRadioStations = useCallback(async (query = '') => {
    setRadioLoading(true);
    setRadioError(null);
    try {
      let results = [];

      if (query.trim()) {
        // User typed a search — search by name globally (drop countrycode for user queries)
        results = await fetchRadioStations({ name: query.trim(), countrycode: undefined });
      } else {
        // ── Default load: curated HBCU callsigns + HBCU-adjacent genres ──
        // HBCU_STATION_NAMES are the actual call letters for known HBCU radio
        // stations. Parallel name-searches are far more productive than the
        // 'hbcu' tag, which almost nothing in Radio Browser uses.
        const nameSearches = HBCU_STATION_NAMES.map(name =>
          fetchRadioStations({ name, limit: '5' })
        );
        const genreSearches = [
          fetchRadioStations({ tag: 'soul',          limit: '25' }),
          fetchRadioStations({ tag: 'gospel',        limit: '25' }),
          fetchRadioStations({ tag: 'urban',         limit: '25' }),
          fetchRadioStations({ tag: 'r&b',           limit: '20' }),
          fetchRadioStations({ tag: 'jazz',          limit: '20' }),
          // Internet-based college / student radio (catches HBCU online-only streams)
          fetchRadioStations({ tag: 'college radio', limit: '30' }),
          fetchRadioStations({ tag: 'student radio', limit: '20' }),
        ];

        // Use allSettled so a single 404/timeout doesn't wipe the whole load
        const settled = await Promise.allSettled([...nameSearches, ...genreSearches]);

        const seen = new Set();
        const hbcuMatches = [];
        const genreMatches = [];

        for (const result of settled) {
          if (result.status !== 'fulfilled') continue;
          for (const s of result.value) {
            if (!s.url_resolved || seen.has(s.stationuuid)) continue;
            seen.add(s.stationuuid);
            // Flag stations that match a known HBCU callsign — pin them first
            const isHBCU = HBCU_STATION_NAMES.some(n =>
              s.name.toUpperCase().startsWith(n)
            );
            if (isHBCU) hbcuMatches.push(s);
            else genreMatches.push(s);
          }
        }

        // Sort each group by favicon presence + click popularity
        const score = s => (s.favicon ? 20 : 0) + Math.log1p(s.clickcount || 0);
        hbcuMatches.sort((a, b)  => score(b) - score(a));
        genreMatches.sort((a, b) => score(b) - score(a));

        // HBCU stations first, then popular genre stations up to 60 total
        results = [...hbcuMatches, ...genreMatches].slice(0, 60);
      }

      setRadioStations(results);
    } catch (err) {
      setRadioError('Could not load stations. Check your connection.');
    } finally {
      setRadioLoading(false);
    }
  }, []);

  // ── Load initial podcasts ────────────────────────────────────────────────
  const loadPodcasts = useCallback(async (query = '') => {
    setPodLoading(true);
    setPodError(null);
    try {
      const term = query.trim() || 'HBCU';
      const results = await searchPodcasts(term);
      setPodcasts(results);
    } catch (err) {
      setPodError('Could not load podcasts. Check your connection.');
    } finally {
      setPodLoading(false);
    }
  }, []);

  useEffect(() => { loadRadioStations(); }, [loadRadioStations]);
  useEffect(() => { loadPodcasts(); },     [loadPodcasts]);

  // Load curated stations from backend (active only)
  useEffect(() => {
    radioAPI.listActive()
      .then(r => setCuratedStations(r.data || []))
      .catch(() => {});
  }, []);

  // Load HBCU creators + curated podcasts
  useEffect(() => {
    setCreatorsLoading(true);
    creatorsAPI.list()
      .then(r => setCreators(r.data?.creators || []))
      .catch(() => setCreators([]))
      .finally(() => setCreatorsLoading(false));
    creatorsAPI.getCuratedPodcasts()
      .then(r => setCuratedPodcasts(r.data?.podcasts || []))
      .catch(() => {});
  }, []);

  // ── Podcast selection + episode fetch ───────────────────────────────────
  const selectPodcast = useCallback(async (podcast) => {
    if (selectedPod?.collectionId === podcast.collectionId) {
      setSelectedPod(null);
      setEpisodes([]);
      return;
    }
    setSelectedPod(podcast);
    setEpisodes([]);
    setEpLoading(true);
    try {
      const eps = await fetchEpisodes(podcast.collectionId);
      setEpisodes(eps);
    } catch {
      toast.error('Could not load episodes');
    } finally {
      setEpLoading(false);
    }
  }, [selectedPod]);

  // ── Radio search (debounced) ─────────────────────────────────────────────
  const radioSearchTimer = useRef(null);
  const handleRadioSearch = (e) => {
    const val = e.target.value;
    setRadioQuery(val);
    setCollegeFilter('all'); // clear college filter on new search
    clearTimeout(radioSearchTimer.current);
    radioSearchTimer.current = setTimeout(() => loadRadioStations(val), 500);
  };

  // ── Podcast search (on enter / button) ──────────────────────────────────
  const handlePodSearch = (e) => {
    e.preventDefault();
    loadPodcasts(podQuery);
    setSelectedPod(null);
    setEpisodes([]);
  };

  // ── Play a radio station ─────────────────────────────────────────────────
  const handlePlayStation = (station) => {
    const isCurrentStation =
      currentTrack?.streamUrl === station.url_resolved ||
      currentTrack?.streamUrl === station.url;

    if (isCurrentStation) { togglePlay(); return; }

    playTrack({
      type:      'radio',
      streamUrl: station.url_resolved || station.url,
      title:     station.name,
      artist:    station.tags?.split(',')[0]?.trim() || '',
      album:     station.country || '',
      imageUrl:  station.favicon || '',
    });
    toast.success(`Now playing ${station.name}`, { duration: 2000 });
  };

  // ── Play a curated station ────────────────────────────────────────────────
  const handlePlayCurated = (station) => {
    if (!station.stream_url) {
      toast.error('No stream URL for this station yet');
      return;
    }
    const isActive = currentTrack?.streamUrl === station.stream_url;
    if (isActive) { togglePlay(); return; }
    playTrack({
      type:      'radio',
      streamUrl: station.stream_url,
      title:     station.name,
      artist:    station.genre || station.college_short || '',
      album:     `${station.city}, ${station.state}`,
      imageUrl:  station.logo_url || '',
    });
    toast.success(`Now playing ${station.name}`, { duration: 2000 });
  };

  // ── Play a podcast episode ───────────────────────────────────────────────
  const handlePlayEpisode = (episode, podcast) => {
    if (!episode.previewUrl) {
      toast.error('Episode audio not available');
      return;
    }
    const isCurrentEp = currentTrack?.streamUrl === episode.previewUrl;
    if (isCurrentEp) { togglePlay(); return; }

    playTrack({
      type:      'podcast',
      streamUrl: episode.previewUrl,
      title:     episode.trackName,
      artist:    podcast.collectionName,
      album:     podcast.artistName || '',
      imageUrl:  podcast.artworkUrl600 || '',
    });
  };

  // ── Expand a YouTube creator — fetch RSS feed ─────────────────────────────
  const handleExpandCreator = useCallback(async (creator) => {
    // Toggle off if same creator
    if (expandedCreator?.id === creator.id) {
      setExpandedCreator(null);
      return;
    }
    setExpandedCreator(creator);

    // Only fetch YouTube videos if we have a channel_id and haven't loaded yet
    if (creator.platform !== 'youtube' || !creator.channel_id) return;
    if (creatorVideos[creator.channel_id]) return; // already loaded

    setVideosLoading(v => ({ ...v, [creator.channel_id]: true }));
    try {
      const r = await creatorsAPI.youtubeFeed(creator.channel_id);
      setCreatorVideos(v => ({ ...v, [creator.channel_id]: r.data?.videos || [] }));
    } catch {
      setCreatorVideos(v => ({ ...v, [creator.channel_id]: [] }));
    } finally {
      setVideosLoading(v => ({ ...v, [creator.channel_id]: false }));
    }
  }, [expandedCreator, creatorVideos]);

  // ── Build school list from loaded creators ────────────────────────────────
  const creatorSchools = (() => {
    const seen = new Set();
    const opts = [];
    for (const c of creators) {
      if (c.school_short && !seen.has(c.school_short)) {
        seen.add(c.school_short);
        opts.push({ short: c.school_short, full: c.school });
      }
    }
    return opts.sort((a, b) => a.short.localeCompare(b.short));
  })();

  const filteredCreators = creators.filter(c => {
    if (creatorPlatform !== 'all' && c.platform !== creatorPlatform) return false;
    if (creatorSchool !== 'all' && c.school_short !== creatorSchool) return false;
    return true;
  });

  // ── Submit a creator ──────────────────────────────────────────────────────
  const handleSubmitCreator = async (e) => {
    e.preventDefault();
    if (!creatorForm.name || !creatorForm.channel_url) {
      toast.error('Name and channel URL are required'); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...creatorForm };
      if (user) { payload.submitter_name = user.name; payload.submitter_email = user.email; }
      await creatorsAPI.submitCreator(payload);
      toast.success('🎉 Your channel has been added to the directory!');
      setCreatorForm(BLANK_CREATOR);
      setShowCreatorModal(false);
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Submit a podcast ──────────────────────────────────────────────────────
  const handleSubmitPodcast = async (e) => {
    e.preventDefault();
    if (!podcastForm.name || !podcastForm.url) {
      toast.error('Podcast name and link are required'); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...podcastForm };
      if (user) { payload.submitter_name = user.name; payload.submitter_email = user.email; }
      await creatorsAPI.submitPodcast(payload);
      toast.success('🎙️ Your podcast has been added to the directory!');
      setPodcastForm(BLANK_PODCAST);
      setShowPodcastModal(false);
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const GENRE_FILTERS = ['all', 'jazz', 'gospel', 'r&b', 'hip-hop', 'urban', 'talk'];

  // Build the dynamic college filter list from whatever stations loaded
  const collegeOptions = (() => {
    const seen = new Set();
    const opts = [];
    for (const s of radioStations) {
      const aff = getAffiliation(s.name);
      if (aff && !seen.has(aff.short)) {
        seen.add(aff.short);
        opts.push(aff);
      }
    }
    return opts.sort((a, b) => a.short.localeCompare(b.short));
  })();

  const filteredStations = radioStations.filter(s => {
    // Genre filter
    if (radioFilter !== 'all') {
      const tags = (s.tags || '').toLowerCase();
      const name = (s.name || '').toLowerCase();
      if (!tags.includes(radioFilter) && !name.includes(radioFilter)) return false;
    }
    // College filter
    if (collegeFilter !== 'all') {
      const aff = getAffiliation(s.name);
      if (!aff || aff.short !== collegeFilter) return false;
    }
    return true;
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Hero header ────────────────────────────────────────────────── */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-dark-slate flex items-center justify-center">
                <Music className="w-4 h-4 text-mint-fresh" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-dark-slate">HBCU Media</h1>
            </div>
            <p className="text-slate-gray text-sm">
              Stream HBCU radio stations and podcasts. Audio keeps playing as you browse.
            </p>
          </div>

          {/* Now-playing pill */}
          {currentTrack && (
            <div
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: 'hsl(var(--secondary))' }}
            >
              <span className="w-2 h-2 rounded-full bg-mint-fresh animate-pulse" />
              <span className="text-dark-slate truncate max-w-[160px]">{currentTrack.title}</span>
            </div>
          )}
        </div>

        {/* ── Tabs ───────────────────────────────────────────────────────── */}
        <div
          className="flex gap-1 p-1 rounded-xl mb-6 w-fit"
          style={{ backgroundColor: 'hsl(var(--secondary))' }}
        >
          {[
            { id: 'creators', icon: Users, label: 'Creators' },
            { id: 'podcast',  icon: Mic2,  label: 'Podcasts'  },
            { id: 'radio',    icon: Radio, label: 'Radio'     },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'text-dark-slate shadow-sm'
                  : 'text-slate-gray hover:text-dark-slate'
              }`}
              style={activeTab === tab.id ? { backgroundColor: 'hsl(var(--card))' } : {}}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            CREATORS TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'creators' && (
          <div>
            {/* CTA banner */}
            <div className="flex items-center justify-between mb-5 p-4 rounded-2xl border border-mint-fresh/25 bg-mint-fresh/5">
              <div>
                <p className="text-sm font-semibold text-dark-slate">Are you an HBCU creator?</p>
                <p className="text-xs text-slate-gray mt-0.5">Add your channel and get discovered by the community instantly.</p>
              </div>
              <Button
                size="sm"
                onClick={() => setShowCreatorModal(true)}
                className="flex items-center gap-1.5 bg-dark-slate text-white hover:bg-slate-700 flex-shrink-0 ml-3"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Your Channel
              </Button>
            </div>

            {/* Platform + school filter row */}
            <div className="flex flex-wrap gap-3 items-start mb-5">
              {/* Platform filter */}
              <div className="flex gap-1.5 flex-wrap">
                {[
                  { id: 'all',      label: 'All Platforms' },
                  { id: 'youtube',  label: '▶ YouTube'     },
                  { id: 'tiktok',   label: '♪ TikTok'      },
                ].map(p => (
                  <button
                    key={p.id}
                    onClick={() => { setCreatorPlatform(p.id); setExpandedCreator(null); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      creatorPlatform === p.id
                        ? 'bg-dark-slate text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* School filter (only when affiliated creators loaded) */}
              {creatorSchools.length > 0 && (
                <div className="flex gap-1.5 flex-wrap items-center">
                  <span className="text-xs text-slate-gray font-medium">🎓</span>
                  <button
                    onClick={() => setCreatorSchool('all')}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      creatorSchool === 'all'
                        ? 'bg-mint-fresh text-dark-slate'
                        : 'bg-slate-100 text-slate-600 hover:bg-mint-fresh/20'
                    }`}
                  >
                    All Schools
                  </button>
                  {creatorSchools.map(s => (
                    <button
                      key={s.short}
                      onClick={() => setCreatorSchool(s.short)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        creatorSchool === s.short
                          ? 'bg-mint-fresh text-dark-slate'
                          : 'bg-slate-100 text-slate-600 hover:bg-mint-fresh/20'
                      }`}
                    >
                      {s.short}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Creator grid */}
            {creatorsLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
                <p className="text-slate-gray text-sm">Loading creators…</p>
              </div>
            ) : filteredCreators.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Users className="w-10 h-10 text-slate-200" />
                <p className="text-slate-gray text-sm">No creators found.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {filteredCreators.map(creator => {
                  const isExpanded = expandedCreator?.id === creator.id;
                  const channelVideos = creator.channel_id ? (creatorVideos[creator.channel_id] || []) : [];
                  const isLoadingVideos = creator.channel_id ? (videosLoading[creator.channel_id] || false) : false;

                  return (
                    <div
                      key={creator.id}
                      className={`rounded-2xl border transition-all overflow-hidden
                        ${isExpanded
                          ? 'border-mint-fresh/40 shadow-md'
                          : 'border-slate-100 hover:border-slate-200 hover:shadow-sm'
                        }`}
                      style={{ backgroundColor: 'hsl(var(--card))' }}
                    >
                      {/* Creator header row — always visible */}
                      <button
                        onClick={() => handleExpandCreator(creator)}
                        className="w-full flex items-center gap-4 p-4 text-left"
                      >
                        {/* Platform avatar */}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-xl
                          ${creator.platform === 'youtube'
                            ? 'bg-red-50'
                            : creator.platform === 'tiktok'
                              ? 'bg-slate-900'
                              : 'bg-gradient-to-br from-purple-500 to-pink-500'
                          }`}
                        >
                          {creator.platform === 'youtube'  && <span className="text-red-500 font-bold text-sm">▶</span>}
                          {creator.platform === 'tiktok'   && <span className="text-white font-bold text-sm">♪</span>}
                          {creator.platform === 'instagram'&& <span className="text-white font-bold text-sm">◎</span>}
                        </div>

                        {/* Creator info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-dark-slate text-sm">{creator.name}</span>
                            {creator.featured && (
                              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                                ⭐ Featured
                              </span>
                            )}
                            {creator.school_short && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-mint-fresh/20 text-dark-slate">
                                🎓 {creator.school_short}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize
                              ${creator.platform === 'youtube'
                                ? 'bg-red-50 text-red-600'
                                : creator.platform === 'tiktok'
                                  ? 'bg-slate-100 text-slate-600'
                                  : 'bg-purple-50 text-purple-600'
                              }`}
                            >
                              {creator.platform}
                            </span>
                          </div>
                          <p className="text-xs text-slate-gray mt-0.5 line-clamp-1">{creator.description}</p>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {(creator.tags || []).slice(0, 3).map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 rounded-full text-[10px] bg-slate-100 text-slate-500 capitalize">
                                {tag}
                              </span>
                            ))}
                            {creator.category && (
                              <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-slate-50 text-slate-400 capitalize">
                                {creator.category}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Expand icon */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4 text-slate-400" />
                            : <ChevronDown className="w-4 h-4 text-slate-400" />
                          }
                        </div>
                      </button>

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                          {creator.platform === 'youtube' ? (
                            <>
                              {/* Visit channel link */}
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-semibold text-slate-gray uppercase tracking-wide">
                                  Recent Videos
                                </span>
                                <a
                                  href={creator.channel_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Visit Channel
                                </a>
                              </div>

                              {!creator.channel_id ? (
                                /* No channel ID — just visit button */
                                <div className="flex flex-col items-center py-6 gap-2">
                                  <p className="text-xs text-slate-gray text-center">
                                    Tap "Visit Channel" to browse their videos on YouTube.
                                  </p>
                                </div>
                              ) : isLoadingVideos ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="w-6 h-6 text-slate-300 animate-spin" />
                                </div>
                              ) : channelVideos.length === 0 ? (
                                <div className="flex flex-col items-center py-6 gap-2">
                                  <p className="text-xs text-slate-gray text-center">
                                    No videos loaded yet. Visit their channel directly.
                                  </p>
                                </div>
                              ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                  {channelVideos.map(video => (
                                    <button
                                      key={video.videoId}
                                      onClick={() => setVideoModal({ embedUrl: video.embedUrl, title: video.title })}
                                      className="group text-left rounded-xl overflow-hidden border border-transparent
                                        hover:border-slate-200 hover:shadow-md transition-all"
                                      style={{ backgroundColor: 'hsl(var(--secondary))' }}
                                    >
                                      {/* Thumbnail */}
                                      <div className="relative aspect-video bg-slate-200 overflow-hidden">
                                        <img
                                          src={video.thumbnail}
                                          alt={video.title}
                                          className="w-full h-full object-cover"
                                          onError={e => { e.currentTarget.style.display='none'; }}
                                        />
                                        {/* Play overlay */}
                                        <div className="absolute inset-0 flex items-center justify-center
                                          bg-black/0 group-hover:bg-black/30 transition-colors">
                                          <div className="w-9 h-9 rounded-full bg-red-500/90 flex items-center justify-center
                                            opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                                            <Play className="w-3.5 h-3.5 text-white ml-0.5" />
                                          </div>
                                        </div>
                                      </div>
                                      {/* Title */}
                                      <div className="p-2">
                                        <p className="text-xs font-medium text-dark-slate line-clamp-2 leading-tight">
                                          {video.title}
                                        </p>
                                        {video.published && (
                                          <p className="text-[10px] text-slate-gray mt-0.5">
                                            {relDate(video.published)}
                                          </p>
                                        )}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            /* TikTok / Instagram — show profile CTA */
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-dark-slate">{creator.handle}</p>
                                <p className="text-xs text-slate-gray mt-0.5">{creator.description}</p>
                              </div>
                              <a
                                href={creator.channel_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors
                                  ${creator.platform === 'tiktok'
                                    ? 'bg-slate-900 text-white hover:bg-slate-800'
                                    : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90'
                                  }`}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                View on {creator.platform === 'tiktok' ? 'TikTok' : 'Instagram'}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            VIDEO MODAL (YouTube embed)
        ══════════════════════════════════════════════════════════════════ */}
        {videoModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.82)' }}
            onClick={() => setVideoModal(null)}
          >
            <div
              className="w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl"
              style={{ backgroundColor: 'hsl(var(--card))' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <p className="text-sm font-semibold text-dark-slate line-clamp-1 pr-4">{videoModal.title}</p>
                <button
                  onClick={() => setVideoModal(null)}
                  className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-slate-600" />
                </button>
              </div>
              {/* 16:9 iframe */}
              <div className="relative" style={{ paddingTop: '56.25%' }}>
                <iframe
                  src={`${videoModal.embedUrl}?autoplay=1&rel=0`}
                  title={videoModal.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            RADIO TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'radio' && (
          <div>
            {/* ── Featured HBCU Stations (curated from backend) ───────────── */}
            {curatedStations.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-dark-slate">🎓 Featured HBCU Stations</span>
                  <span className="text-xs text-slate-gray">· Curated</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {curatedStations.map(station => {
                    const isActive  = currentTrack?.streamUrl === station.stream_url;
                    const hasError  = isActive && !!audioError;
                    const cLetters  = getCallLetters(station.name);
                    const cCollege  = station.college_short || '';
                    return (
                      <div
                        key={station.id}
                        className={`group relative flex flex-col rounded-2xl overflow-hidden transition-all border cursor-pointer
                          ${hasError
                            ? 'border-red-300 shadow-md ring-2 ring-red-200'
                            : isActive
                              ? 'border-mint-fresh shadow-md ring-2 ring-mint-fresh/30'
                              : 'border-transparent hover:border-slate-200 hover:shadow-md'}`}
                        style={{ backgroundColor: 'hsl(var(--card))' }}
                        onClick={() => handlePlayCurated(station)}
                      >
                        {/* Artwork */}
                        <div className="relative aspect-square overflow-hidden">
                          {station.logo_url
                            ? <>
                                <div
                                  className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2"
                                  style={{ background: fallbackGradient(cLetters) }}
                                >
                                  <span className="text-mint-fresh font-black tracking-tight leading-none"
                                    style={{ fontSize: 'clamp(1.4rem, 5cqw, 2rem)' }}>
                                    {cLetters}
                                  </span>
                                  {cCollege && (
                                    <span className="text-white/60 font-medium text-center leading-tight"
                                      style={{ fontSize: 'clamp(0.55rem, 2cqw, 0.7rem)' }}>
                                      {cCollege}
                                    </span>
                                  )}
                                </div>
                                <img src={station.logo_url} alt={station.name}
                                  className="absolute inset-0 w-full h-full object-cover"
                                  onError={e => { e.currentTarget.style.display = 'none'; }} />
                              </>
                            : (
                              <div
                                className="w-full h-full flex flex-col items-center justify-center gap-1.5 px-2"
                                style={{ background: fallbackGradient(cLetters) }}
                              >
                                <span className="text-mint-fresh font-black tracking-tight leading-none text-center"
                                  style={{ fontSize: 'clamp(1.6rem, 6cqw, 2.25rem)' }}>
                                  {cLetters}
                                </span>
                                {cCollege && (
                                  <span className="text-white/60 font-medium text-center leading-tight"
                                    style={{ fontSize: 'clamp(0.6rem, 2.2cqw, 0.75rem)' }}>
                                    {cCollege}
                                  </span>
                                )}
                              </div>
                            )}

                          {/* LIVE badge */}
                          {!hasError && (
                            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/90 text-white uppercase tracking-wide">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                              Live
                            </span>
                          )}

                          {/* Error overlay */}
                          {hasError && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-dark-slate/70 gap-1 p-2">
                              <AlertCircle className="w-5 h-5 text-red-400" />
                              <p className="text-[10px] text-white text-center leading-tight">Stream unavailable</p>
                            </div>
                          )}

                          {/* Play overlay */}
                          <div className="absolute inset-0 flex items-center justify-center
                            bg-dark-slate/0 group-hover:bg-dark-slate/40 transition-colors">
                            <div className={`w-12 h-12 rounded-full bg-dark-slate flex items-center justify-center shadow-lg
                              transition-all ${isActive ? 'opacity-100 scale-100' : 'opacity-0 group-hover:opacity-100 group-hover:scale-100'}`}>
                              {isActive && isPlaying
                                ? <Pause className="w-5 h-5 text-mint-fresh" />
                                : <Play  className="w-5 h-5 text-mint-fresh ml-0.5" />}
                            </div>
                          </div>
                        </div>

                        {/* Info */}
                        <div className="p-3 flex flex-col gap-1">
                          <p className="font-semibold text-dark-slate text-sm line-clamp-1">{station.name}</p>
                          {station.college_short && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold
                              bg-mint-fresh/20 text-dark-slate w-fit max-w-full">
                              🎓 <span className="truncate">{station.college_short}</span>
                            </span>
                          )}
                          {station.genre && (
                            <p className="text-xs text-slate-gray truncate">{station.genre}</p>
                          )}
                          {station.frequency && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-slate-100 text-slate-500 w-fit">
                              {station.frequency}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 border-t border-slate-100" />
              </div>
            )}

            {/* Search + refresh */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                <Input
                  ref={radioInputRef}
                  value={radioQuery}
                  onChange={handleRadioSearch}
                  placeholder="Search stations…"
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadRadioStations(radioQuery)}
                disabled={radioLoading}
                aria-label="Refresh stations"
              >
                <RefreshCw className={`w-4 h-4 ${radioLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Genre filter pills */}
            <div className="flex gap-2 flex-wrap mb-3">
              {GENRE_FILTERS.map(g => (
                <button
                  key={g}
                  onClick={() => setRadioFilter(g)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
                    radioFilter === g
                      ? 'bg-dark-slate text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {g === 'all' ? 'All Genres' : g}
                </button>
              ))}
            </div>

            {/* College filter pills — only shown when affiliated stations loaded */}
            {collegeOptions.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-5 items-center">
                <span className="text-xs text-slate-gray font-medium mr-1">🎓 College:</span>
                <button
                  onClick={() => setCollegeFilter('all')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    collegeFilter === 'all'
                      ? 'bg-mint-fresh text-dark-slate'
                      : 'bg-slate-100 text-slate-600 hover:bg-mint-fresh/20'
                  }`}
                >
                  All
                </button>
                {collegeOptions.map(opt => (
                  <button
                    key={opt.short}
                    onClick={() => setCollegeFilter(opt.short)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      collegeFilter === opt.short
                        ? 'bg-mint-fresh text-dark-slate'
                        : 'bg-slate-100 text-slate-600 hover:bg-mint-fresh/20'
                    }`}
                  >
                    {opt.short}
                  </button>
                ))}
              </div>
            )}

            {/* Station grid — Radio Browser results */}
            {!radioQuery && curatedStations.length > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-dark-slate">📻 More Stations</span>
                <span className="text-xs text-slate-gray">· via Radio Browser</span>
              </div>
            )}
            {radioLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
                <p className="text-slate-gray text-sm">Loading stations…</p>
              </div>
            ) : radioError ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <AlertCircle className="w-8 h-8 text-red-400" />
                <p className="text-slate-gray text-sm">{radioError}</p>
                <Button variant="outline" size="sm" onClick={() => loadRadioStations(radioQuery)}>
                  Try again
                </Button>
              </div>
            ) : filteredStations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Radio className="w-10 h-10 text-slate-200" />
                <p className="text-slate-gray text-sm">No stations found.</p>
                <Button variant="outline" size="sm" onClick={() => { setRadioQuery(''); setRadioFilter('all'); setCollegeFilter('all'); loadRadioStations(''); }}>
                  Clear filters
                </Button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-gray mb-3">
                  {filteredStations.length} station{filteredStations.length !== 1 ? 's' : ''}
                  {!radioQuery && ' · Powered by Radio Browser'}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {filteredStations.map(station => {
                    const streamUrl  = station.url_resolved || station.url;
                    const isActive   = currentTrack?.streamUrl === streamUrl;
                    const hasError   = isActive && !!audioError;
                    const affiliation = getAffiliation(station.name);
                    return (
                      <StationCard
                        key={station.stationuuid}
                        station={station}
                        isActive={isActive}
                        hasError={hasError}
                        affiliation={affiliation}
                        isPlaying={isPlaying}
                        onPlay={() => handlePlayStation(station)}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PODCASTS TAB
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'podcast' && (
          <div>
            {/* Back button when a podcast is selected */}
            {selectedPod ? (
              <>
                <button
                  onClick={() => { setSelectedPod(null); setEpisodes([]); }}
                  className="flex items-center gap-1 text-sm text-slate-gray hover:text-dark-slate mb-4 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to podcasts
                </button>

                {/* Podcast header */}
                <div className="flex gap-4 mb-6 p-4 rounded-2xl" style={{ backgroundColor: 'hsl(var(--card))' }}>
                  {selectedPod.artworkUrl600 && (
                    <img
                      src={selectedPod.artworkUrl600}
                      alt={selectedPod.collectionName}
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-heading font-bold text-dark-slate text-lg leading-tight line-clamp-2">
                      {selectedPod.collectionName}
                    </h2>
                    <p className="text-slate-gray text-sm mt-0.5">{selectedPod.artistName}</p>
                    {selectedPod.primaryGenreName && (
                      <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs bg-mint-fresh/20 text-dark-slate font-medium">
                        {selectedPod.primaryGenreName}
                      </span>
                    )}
                    {selectedPod.collectionViewUrl && (
                      <a
                        href={selectedPod.collectionViewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 ml-2 text-xs text-slate-gray hover:text-dark-slate"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Apple Podcasts
                      </a>
                    )}
                  </div>
                </div>

                {/* Episode list */}
                {epLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <Loader2 className="w-7 h-7 text-slate-300 animate-spin" />
                    <p className="text-slate-gray text-sm">Loading episodes…</p>
                  </div>
                ) : episodes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <Mic2 className="w-10 h-10 text-slate-200" />
                    <p className="text-slate-gray text-sm">No episodes available.</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-slate-gray mb-1">{episodes.length} episodes</p>
                    {episodes.map(ep => {
                      const isActive = currentTrack?.streamUrl === ep.previewUrl;
                      return (
                        <EpisodeRow
                          key={ep.trackId || ep.trackName}
                          episode={ep}
                          isActive={isActive}
                          isPlaying={isActive && isPlaying}
                          onPlay={() => handlePlayEpisode(ep, selectedPod)}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Curated HBCU Podcasts (community-submitted + approved) */}
                {curatedPodcasts.length > 0 && (
                  <div className="mb-7">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-semibold text-dark-slate">🎙️ Featured HBCU Podcasts</span>
                      <span className="text-xs text-slate-gray">· Community curated</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {curatedPodcasts.map(pod => (
                        <a
                          key={pod.id}
                          href={pod.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-3 p-3 rounded-xl border border-slate-100
                            hover:border-mint-fresh/40 hover:shadow-sm transition-all"
                          style={{ backgroundColor: 'hsl(var(--card))' }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-mint-fresh/15 flex items-center justify-center flex-shrink-0">
                            <Mic2 className="w-5 h-5 text-mint-fresh" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-dark-slate line-clamp-1">{pod.name}</p>
                            {pod.host && <p className="text-xs text-slate-gray mt-0.5">{pod.host}</p>}
                            {pod.school_short && (
                              <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-mint-fresh/20 text-dark-slate">
                                🎓 {pod.school_short}
                              </span>
                            )}
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                        </a>
                      ))}
                    </div>
                    <div className="mt-5 border-t border-slate-100" />
                  </div>
                )}

                {/* Submit podcast CTA */}
                <div className="flex items-center justify-between mb-5 p-4 rounded-2xl border border-mint-fresh/25 bg-mint-fresh/5">
                  <div>
                    <p className="text-sm font-semibold text-dark-slate">Have an HBCU podcast?</p>
                    <p className="text-xs text-slate-gray mt-0.5">It goes live in the Featured Podcasts section instantly.</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowPodcastModal(true)}
                    className="flex items-center gap-1.5 bg-dark-slate text-white hover:bg-slate-700 flex-shrink-0 ml-3"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Submit Podcast
                  </Button>
                </div>

                {/* Podcast search */}
                <form onSubmit={handlePodSearch} className="flex gap-2 mb-5">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                    <Input
                      ref={podInputRef}
                      value={podQuery}
                      onChange={e => setPodQuery(e.target.value)}
                      placeholder="Search HBCU podcasts…"
                      className="pl-9"
                    />
                  </div>
                  <Button type="submit" disabled={podLoading}>
                    {podLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </form>

                {/* Quick-search pills */}
                <div className="flex gap-2 flex-wrap mb-5">
                  {HBCU_PODCAST_TERMS.map(term => (
                    <button
                      key={term}
                      onClick={() => { setPodQuery(term); loadPodcasts(term); }}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-mint-fresh/20 hover:text-dark-slate transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>

                {/* Podcast grid */}
                {podLoading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
                    <p className="text-slate-gray text-sm">Loading podcasts…</p>
                  </div>
                ) : podError ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                    <p className="text-slate-gray text-sm">{podError}</p>
                    <Button variant="outline" size="sm" onClick={() => loadPodcasts(podQuery)}>
                      Try again
                    </Button>
                  </div>
                ) : podcasts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Mic2 className="w-10 h-10 text-slate-200" />
                    <p className="text-slate-gray text-sm">No podcasts found for that search.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-slate-gray mb-3">
                      {podcasts.length} podcast{podcasts.length !== 1 ? 's' : ''} · Tap to see episodes
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      {podcasts.map(pod => (
                        <PodcastCard
                          key={pod.collectionId}
                          podcast={pod}
                          isSelected={selectedPod?.collectionId === pod.collectionId}
                          onClick={() => selectPodcast(pod)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Bottom padding for persistent player on mobile */}
        <div className="h-4 md:hidden" />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          SUBMIT CREATOR MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {showCreatorModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowCreatorModal(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: 'hsl(var(--card))' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h2 className="font-heading font-bold text-dark-slate">Add Your Channel</h2>
                <p className="text-xs text-slate-gray mt-0.5">Goes live in the directory immediately.</p>
              </div>
              <button
                onClick={() => setShowCreatorModal(false)}
                className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X className="w-3.5 h-3.5 text-slate-600" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitCreator} className="p-5 flex flex-col gap-4">
              {/* Platform */}
              <div>
                <label className="block text-xs font-semibold text-slate-gray mb-1.5 uppercase tracking-wide">Platform *</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'youtube',   label: '▶ YouTube'   },
                    { id: 'tiktok',    label: '♪ TikTok'    },
                    { id: 'instagram', label: '◎ Instagram'  },
                    { id: 'other',     label: '… Other'      },
                  ].map(p => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setCreatorForm(f => ({ ...f, platform: p.id }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        creatorForm.platform === p.id
                          ? 'bg-dark-slate text-white border-dark-slate'
                          : 'border-slate-200 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name + Handle */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Channel / Creator Name *</label>
                  <Input
                    value={creatorForm.name}
                    onChange={e => setCreatorForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. HBCU Gameday"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Handle</label>
                  <Input
                    value={creatorForm.handle}
                    onChange={e => setCreatorForm(f => ({ ...f, handle: e.target.value }))}
                    placeholder="@YourHandle"
                  />
                </div>
              </div>

              {/* URL */}
              <div>
                <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Channel / Profile URL *</label>
                <Input
                  type="url"
                  value={creatorForm.channel_url}
                  onChange={e => setCreatorForm(f => ({ ...f, channel_url: e.target.value }))}
                  placeholder="https://www.youtube.com/@YourChannel"
                  required
                />
              </div>

              {/* School + Category */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">HBCU Affiliation</label>
                  <select
                    value={creatorForm.school}
                    onChange={e => setCreatorForm(f => ({ ...f, school: e.target.value, school_short: e.target.value.split(' ')[0] }))}
                    className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white text-dark-slate focus:outline-none focus:ring-2 focus:ring-mint-fresh/40"
                  >
                    <option value="">Select school…</option>
                    {HBCU_SCHOOLS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Category</label>
                  <select
                    value={creatorForm.category}
                    onChange={e => setCreatorForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white text-dark-slate focus:outline-none focus:ring-2 focus:ring-mint-fresh/40"
                  >
                    <option value="">Select…</option>
                    {['Entertainment','Sports','News & Culture','Education','Lifestyle','Music','Comedy','Business','Other'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Brief Description</label>
                <textarea
                  value={creatorForm.description}
                  onChange={e => setCreatorForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What's your content about? (2–3 sentences)"
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white text-dark-slate resize-none focus:outline-none focus:ring-2 focus:ring-mint-fresh/40"
                />
              </div>

              {/* Contact (only if not logged in) */}
              {!user && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Your Name</label>
                    <Input
                      value={creatorForm.submitter_name}
                      onChange={e => setCreatorForm(f => ({ ...f, submitter_name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Your Email</label>
                    <Input
                      type="email"
                      value={creatorForm.submitter_email}
                      onChange={e => setCreatorForm(f => ({ ...f, submitter_email: e.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCreatorModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 flex items-center gap-2">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Channel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          SUBMIT PODCAST MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {showPodcastModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowPodcastModal(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: 'hsl(var(--card))' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h2 className="font-heading font-bold text-dark-slate">Submit Your Podcast</h2>
                <p className="text-xs text-slate-gray mt-0.5">Goes live in the Featured Podcasts section immediately.</p>
              </div>
              <button
                onClick={() => setShowPodcastModal(false)}
                className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X className="w-3.5 h-3.5 text-slate-600" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitPodcast} className="p-5 flex flex-col gap-4">
              {/* Platform */}
              <div>
                <label className="block text-xs font-semibold text-slate-gray mb-1.5 uppercase tracking-wide">Platform *</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'apple',   label: '🎵 Apple Podcasts' },
                    { id: 'spotify', label: '🟢 Spotify'         },
                    { id: 'youtube', label: '▶ YouTube'           },
                    { id: 'other',   label: '… Other / RSS'       },
                  ].map(p => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setPodcastForm(f => ({ ...f, platform: p.id }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        podcastForm.platform === p.id
                          ? 'bg-dark-slate text-white border-dark-slate'
                          : 'border-slate-200 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name + Host */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Podcast Name *</label>
                  <Input
                    value={podcastForm.name}
                    onChange={e => setPodcastForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. HBCU Stories"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Host Name</label>
                  <Input
                    value={podcastForm.host}
                    onChange={e => setPodcastForm(f => ({ ...f, host: e.target.value }))}
                    placeholder="Host / Creator"
                  />
                </div>
              </div>

              {/* URL */}
              <div>
                <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Podcast Link *</label>
                <Input
                  type="url"
                  value={podcastForm.url}
                  onChange={e => setPodcastForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://podcasts.apple.com/..."
                  required
                />
              </div>

              {/* School */}
              <div>
                <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">HBCU Affiliation</label>
                <select
                  value={podcastForm.school}
                  onChange={e => setPodcastForm(f => ({ ...f, school: e.target.value, school_short: e.target.value.split(' ')[0] }))}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white text-dark-slate focus:outline-none focus:ring-2 focus:ring-mint-fresh/40"
                >
                  <option value="">Select school…</option>
                  {HBCU_SCHOOLS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Brief Description</label>
                <textarea
                  value={podcastForm.description}
                  onChange={e => setPodcastForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What's your podcast about? (2–3 sentences)"
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white text-dark-slate resize-none focus:outline-none focus:ring-2 focus:ring-mint-fresh/40"
                />
              </div>

              {/* Contact (only if not logged in) */}
              {!user && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Your Name</label>
                    <Input
                      value={podcastForm.submitter_name}
                      onChange={e => setPodcastForm(f => ({ ...f, submitter_name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-gray mb-1 uppercase tracking-wide">Your Email</label>
                    <Input
                      type="email"
                      value={podcastForm.submitter_email}
                      onChange={e => setPodcastForm(f => ({ ...f, submitter_email: e.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowPodcastModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 flex items-center gap-2">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Podcast
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
